import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  AdminReducer,
  AdminReducerPath,
  AdminSlice
} from '@unistocks/features/admin/store/admin.slice';
import {
  PortfolioReducer,
  PortfolioReducerPath,
  PortfolioSlice
} from '@unistocks/features/portfolios/store/portfolio.slice';
import {
  SavingsGoalReducer,
  SavingsGoalReducerPath,
  SavingsGoalSlice
} from '@unistocks/features/savings-goals/store/savings-goals.slice';
import {
  WatchlistReducer,
  WatchlistReducerPath,
  WatchlistSlice
} from '@unistocks/features/watchlists/store/watchlist.slice';
import { REDUX_STORAGE_VERSION, STORAGE_ID } from '@unistocks/shared/consts';
import { storage } from '@unistocks/shared/storage';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  PersistConfig,
  REGISTER,
  REHYDRATE,
  Storage,
  persistReducer,
  persistStore
} from 'redux-persist';
import { AppReducer, AppReducerPath, AppState } from './root.slice';

const reduxStorage: Storage = {
  setItem: (key, value) => {
    storage.set(key, value);

    return Promise.resolve(true);
  },

  getItem: key => {
    const value = storage.getString(key);

    return Promise.resolve(value);
  },

  removeItem: key => {
    storage.remove(key);

    return Promise.resolve();
  }
};

const createPersistConfig = <State>(
  key: string,
  options?: Partial<PersistConfig<State>>
): PersistConfig<State> => ({
  storage: reduxStorage,
  throttle: 1000,
  version: REDUX_STORAGE_VERSION,
  key,
  ...options
});

const rootPersistConfig = createPersistConfig<AppState>(`${STORAGE_ID}:redux-root`, {
  blacklist: ['colorScheme']
});

const portfolioPersistConfig = createPersistConfig<PortfolioSlice>(
  `${STORAGE_ID}:redux-portfolio`
);

const watchlistPersistConfig = createPersistConfig<WatchlistSlice>(
  `${STORAGE_ID}:redux-watchlist`
);

const savingsGoalPersistConfig = createPersistConfig<SavingsGoalSlice>(
  `${STORAGE_ID}:redux-savings-goal`
);

const adminPersistConfig = createPersistConfig<AdminSlice>(`${STORAGE_ID}:redux-admin`);

const rootReducer = combineReducers({
  [AppReducerPath]: persistReducer(rootPersistConfig, AppReducer),
  [PortfolioReducerPath]: persistReducer(portfolioPersistConfig, PortfolioReducer),
  [WatchlistReducerPath]: persistReducer(watchlistPersistConfig, WatchlistReducer),
  [SavingsGoalReducerPath]: persistReducer(savingsGoalPersistConfig, SavingsGoalReducer),

  [AdminReducerPath]: persistReducer(adminPersistConfig, AdminReducer)
});

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
        }
      }),
    devTools: false,
    preloadedState
  });
};

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];

export const store = setupStore();
export const reduxPersistor = persistStore(store);
